<template>
  <SogPage title="Verwaltung (beta)">
    <SogCard>
      <SogCardActions :is-toolbar="true">
        <SogTextBox
          v-model="_listPage.personName"
          placeholder="Kunde"
          :interval-modified="onSearch"
        />
        <div style="width: 20%">
          <SogDropDownList
            v-model="_listPage.statusManagement"
            placeholder="Status"
            :data-source="_lstStatus"
            :fields="_fields"
            popup-width="auto"
          />
        </div>
        <SogCardActionButton
          :kind="SogButtonKind.Search"
          style="align-self: flex-end"
          @click="search"
        />
        <SogCardActionButton
          :kind="SogButtonKind.Back"
          style="align-self: flex-end"
        />
      </SogCardActions>
      <SogCardContent>
        <ManagementListComponent
          ref="_managementList"
          @selected="onSelected"
        />
      </SogCardContent>
    </SogCard>
    <SogCard v-show="_personId">
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton
          v-show="_currentInsuranceCancelationId"
          mdi-icon="mdi-information"
          @click="onDetails"
        />
        <SogTextBox
          v-model="_insuranceNumber"
          placeholder="VR-Nummer"
          :interval-modified="onFilter"
        />
        <SogCardActionButton
          :kind="SogButtonKind.Search"
          style="align-self: flex-end"
          @click="filter"
        />
        <SogBadge
          v-show="_status === 'ToAllow' && _actionVisible"
          :value="_countSelected"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionButton
            mdi-icon="mdi-check-circle"
            tooltip="Genehmigen"
            @click="onApprove"
          />
        </SogBadge>
        <SogBadge
          v-show="_status === 'ToAllow' && _actionVisible"
          :value="_countSelected"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionButton
            mdi-icon="mdi-close-circle"
            tooltip="Ablehnen"
            @click="onReject"
          >
            <SogDialog
              ref="_dialogIgnore"
              target="#app"
              :dialog-buttons="SogDialogButtons.OkCancel"
              @click-ok="onOkReject"
              @click-cancel="onCancel"
            >
              <SogTextBox
                v-model="_reason"
                :multiline="true"
                width="100%"
                placeholder="Grund fürs Ablehnen"
              />
            </SogDialog>
          </SogCardActionButton>
        </SogBadge>
        <SogBadge
          v-show="_status === 'Allowed' && _actionVisible"
          :value="_countSelected"
          badge-style="primary"
          badge-type="notification"
          overlap="true"
        >
          <SogCardActionButton
            mdi-icon="mdi-currency-eur"
            tooltip="Gutschrift/Rechnung"
            @click="onCreateInvoice"
          />
        </SogBadge>
      </SogCardActions>
      <SogCardContent>
        <ManagementListDetailsComponent
          ref="_grid"
          :selected="onSelectedRow"
          @selected="rowSelected"
        />
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import SogPage from "@/shared/ui/SogPage.vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import ManagementListComponent from "@/modules/InsuranceCancelation/components/ManagementListComponent.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import {computed, onMounted, reactive, ref, watch} from "vue";
import {useInsuranceCancelationStore} from "@/modules/InsuranceCancelation/store";
import {storeToRefs} from "pinia";
import InsuranceCancelation from "@/shared/model/InsuranceCancelation";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import {useNavigationStore} from "@/modules/Navigation/store";
import SogBadge from "@/shared/ui/SogBadge.vue";
import {IdType} from "@/shared/model/idType";
import ManagementListDetailsComponent
  from "@/modules/InsuranceCancelation/components/ManagementListDetailsComponent.vue";
import notificationService from "@/shared/services/NotificationService";
import apiInsuranceCancelation from "@/modules/InsuranceCancelation/api";
import SogDialog from "@/shared/ui/SogDialog.vue";
import {SogDialogButtons} from "@/shared/ui/types/SogDialogButtons";

const _managementList = ref<InstanceType<typeof ManagementListComponent> | null>(null)
const _insuranceCancellationStore = useInsuranceCancelationStore()
const {listPage: _listPage} = storeToRefs(_insuranceCancellationStore);
const _grid = ref<InstanceType<typeof ManagementListDetailsComponent> | null>(null)
const _navigationStore = useNavigationStore();
const _personId = ref();
const _status = ref();
const _currentInsuranceCancelationId = ref("");
const _lstStatus = reactive([
  {id: "ToAllow", value: "Zur Prüfung"},
  {id: "Allowed", value: "Genehmigt"},
  {id: "Ordered", value: "Gutgeschrieben"},
  {id: "Rejected", value: "Abgelehnt"},
  {id: "all", value: "Alle"},
]);
const _fields = {text: "value", value: "id"};
const _insuranceNumber = ref("");
const _countSelected = ref(0);
const _selectedItems = reactive<InsuranceCancelation[]>([])
const _reason = ref("")
const _dialogIgnore = ref<InstanceType<typeof SogDialog> | null>(null)
const _actionVisible = computed(() => {

  return _countSelected.value > 0;

})
const selectedIds = reactive<string[]>([])

function onSelectedRow(value: InsuranceCancelation[]) {
  _selectedItems.length = 0;
  selectedIds.length = 0;
  _selectedItems.push(...value);
  selectedIds.push(..._selectedItems.map(x=>x.id!))
}
function onDetails() {
  if (_navigationStore.currentInsuranceCancelationId) {
    _navigationStore.showObject(IdType.InsuranceCancelation, _navigationStore.currentInsuranceCancelationId)
  }
}

function search() {
  if (!_managementList.value || !_managementList.value.adaptor) return;

  _managementList.value?.adaptor.removePredicate("personName");
  if (_listPage.value.personName)
    _managementList.value?.adaptor.addPredicate(
        "personName",
        "contains",
        _listPage.value.personName.toUpperCase()
    );
  _managementList.value?.adaptor?.removePredicate("cancelationStatus");
  if (_listPage.value.statusManagement !== "all") {
    _managementList.value?.adaptor.addPredicate(
        "cancelationStatus",
        "equal",
        _listPage.value.statusManagement
    );
  }


  _managementList.value?.refresh();
}

function onSelected(value?: InsuranceCancelation) {
  _personId.value = value?.personId;
  _status.value = value?.cancelationStatus;
}

function rowSelected(data: any | undefined) {
  _currentInsuranceCancelationId.value = data?.id
}

function filter() {
  if (!_grid.value || !_grid.value.adaptor) return;
  _grid.value?.adaptor.removePredicate("personId");
  if (_personId.value) {
    _grid.value?.adaptor.addPredicate(
        "personId",
        "equal",
        _personId.value
    );
  }
  _grid.value?.adaptor.removePredicate("cancelationStatus");
  if (_status.value) {
    _grid.value?.adaptor.addPredicate(
        "cancelationStatus",
        "equal",
        _status.value
    );
  }
  _grid.value?.adaptor.removePredicate("number");
  if (_insuranceNumber.value) {
    _grid.value?.adaptor.addPredicate(
        "number",
        "contains",
        _insuranceNumber.value.toUpperCase()
    );
  }
  _grid.value?.refresh();
}

async function onApprove() {
  notificationService.showYesNo("Genehmigen", "Möchten Sie wirklich die Genehmigung vornehmen", () => {
    apiInsuranceCancelation.approve("",selectedIds).then(() => {
      notificationService.success("Versicherung erfolgreich genehmigt")
      _grid.value?.refresh();
      _managementList.value?.refresh();
    });
  });
}
function onCancel() {
  _reason.value = ""
  _dialogIgnore.value?.hide()
}
function onReject() {
  _dialogIgnore.value?.show()
}
async function onOkReject() {

  if (!_reason.value) {
    notificationService.error("Bitte einen Grund eingeben")
    return
  }
  const res = await apiInsuranceCancelation.reject(_reason.value,selectedIds)
  if (res) {
    notificationService.success("Versicherung erfolgreich abgelehnt")
    _reason.value = ""
    _dialogIgnore.value?.hide()
    _grid.value?.refresh();
    _managementList.value?.refresh();
  }

}
/*function onReject() {
  notificationService.showYesNo("Ablehnen", "Möchten Sie wirklich die Ablehnung vornehmen", () => {
    apiInsuranceCancelation.reject("",selectedIds).then(() => {
      _grid.value?.refresh();
      _managementList.value?.refresh();
    });
  });
}*/

function onCreateInvoice() {
  notificationService.showYesNo("Gutschrift/Rechnung", "Möchten Sie wirklich die Gutschrift/Rechnung erstellen", () => {
    apiInsuranceCancelation.createInvoice("",selectedIds).then(() => {
      notificationService.success("Gutschrift/Rechnung erfolgreich erstellt")
      _grid.value?.refresh();
      _managementList.value?.refresh();
    });
  });
}

onMounted(() => {
  search();
});

watch(_listPage.value, () => {
  search();
});
watch([_personId, _status], () => {
  _selectedItems.length = 0;
  selectedIds.length = 0;
  filter();
});

watch(_selectedItems, () => {
  _countSelected.value = _selectedItems.length;
});


function onSearch() {
  search();
}

function onFilter() {
  filter();
}

</script>

<style scoped>

</style>
